import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getProductMachineryEquipmentList,
  addMachineryEquipmentToProduct,
  updateProductMachineryEquipment,
  deleteProductMachineryEquipment,
} from "../../../../../../apis/Product/ProductMachineryEquipment";
// import { getProdProductionMachineVehicleDropdown } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import useBonsaiData from "../../../../../../hooks/useBonsaiData";

const ProdMachineTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  // Use cached Bonsai data instead of individual API call
  const { data: dropdownUsedFor, isLoading: isBonsaiLoading } = useBonsaiData();

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getProductMachineryEquipmentList({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["production_machinery"]?.value || row.production_machinery;
        if (materialValue) {
          const selectedMaterial = dropdownUsedFor.find(
            (option) => option?.value === materialValue
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownUsedFor]);

  const dynamicPlaceholders = useMemo(
    () => ({
      production_machinery: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedMaterial) {
            // For existing rows
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit, // Use a special key for new rows
              }));
            }
          }
        },
        dropdownDataKey: "production_machinery",
      },
    }),
    []
  );
  const handleEdit = useCallback(
    (editedItem) => {
      const foundProduct = dropdownUsedFor?.find(
        (option) => option.name === editedItem.production_machinery.value
      );
      const product_code = foundProduct
        ? foundProduct["code"]
        : editedItem.product_code;
      updateProductMachineryEquipment({
        productId,
        componentId: editedItem.id,
        ...editedItem,
        production_machinery: editedItem.production_machinery.value,
        product_code: product_code,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, data, setData, dropdownUsedFor]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const foundProduct = dropdownUsedFor?.find(
        (option) => option.name === newItem.production_machinery.value
      );

      const product_code = foundProduct ? foundProduct["code"] : "";
      addMachineryEquipmentToProduct({
        productId,
        ...newItem,
        production_machinery: newItem.production_machinery.value,
        product_code: product_code,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, dropdownUsedFor]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductMachineryEquipment({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "machine",
        header: t("MACHINE"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "120px",
      },
      {
        accessor: "production_machinery",
        header: `${t("ENERGY_TYPE")}*`,
        type: "dropdown",
        dropdownData: dropdownUsedFor,
        disableSortBy: true,
      },
      {
        accessor: "qty",
        header: t("QTY_FUNC_UNIT"),
        type: "number",
        tipHeader: "Tip",
        tipBody: `${t(
          "TIP_QTY_FUNC_COMP"
        )} Some selected items do not have units; "unit" will be shown as a placeholder.`,
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: (rowId, rowData) => {
          const rowType =
            rowData?.production_machinery?.value ||
            rowData?.["production_machinery"]?.value;
          return rowType
            ? dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId] || "unit";
        },
      },
      {
        accessor: "manuel",
        header: (props) => {
          const rowId = props.row?.original?.id;
          return `${t("Manual kgCO₂")} /${unitMaterials[rowId] || "unit"}`;
        },
        type: "number",
        tipHeader: "Tip",
        tipBody: `${t(
          "TIP_MANUEL_PROD"
        )} Some selected items do not have units; "unit" will be shown as a placeholder.`,
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: (rowId, rowData) => {
          const rowType =
            rowData?.type?.value || rowData?.["production_machinery"]?.value;
          return rowType
            ? "kgCO₂/" +
                dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId]
            ? "kgCO₂/" + unitMaterials[rowId]
            : "kgCO₂/unit";
        },
        optional: true,
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [dropdownUsedFor, t, unitMaterials]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      production_machinery: dropdownUsedFor,
    }),
    [dropdownUsedFor]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProdMachineTable;
